import type { ProjectFolder } from "../../api/projectFolders";
import type { Project } from "../../types/project";

/** A folder id, or null for the top level ("All projects"). */
export type FolderKey = number | null;

export interface FolderIndex {
  byId: ReadonlyMap<number, ProjectFolder>;
  /** Children of each folder (null = top level), sorted by name. */
  childrenOf: ReadonlyMap<FolderKey, ProjectFolder[]>;
}

const byName = (a: ProjectFolder, b: ProjectFolder) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) || a.id - b.id;

/** Pinned folders lead, newest pin first (a just-pinned folder has no stamp yet
 *  and counts as newest), then everything by name. Same rule as project pins. */
const byPinThenName = (a: ProjectFolder, b: ProjectFolder) => {
  if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
  if (a.is_pinned && b.is_pinned) {
    const at = a.pinned_at ? Date.parse(a.pinned_at) : Infinity;
    const bt = b.pinned_at ? Date.parse(b.pinned_at) : Infinity;
    if (at !== bt) return bt - at;
  }
  return byName(a, b);
};

export function buildFolderIndex(folders: ProjectFolder[]): FolderIndex {
  const byId = new Map<number, ProjectFolder>();
  folders.forEach((f) => byId.set(f.id, f));
  const childrenOf = new Map<FolderKey, ProjectFolder[]>();
  folders.forEach((f) => {
    // A parent that is missing (mid-refetch after a delete) counts as root
    // rather than making the folder vanish.
    const parent: FolderKey = f.parent !== null && byId.has(f.parent) ? f.parent : null;
    const list = childrenOf.get(parent);
    if (list) list.push(f);
    else childrenOf.set(parent, [f]);
  });
  childrenOf.forEach((list) => list.sort(byPinThenName));
  return { byId, childrenOf };
}

/** A project's folder as the tree knows it; an unknown id counts as root. */
export function normalizeFolderId(index: FolderIndex, raw: number | null | undefined): FolderKey {
  return raw !== null && raw !== undefined && index.byId.has(raw) ? raw : null;
}

/** Root-most first, ending at `id`. Empty for the top level. */
export function folderPath(index: FolderIndex, id: FolderKey): ProjectFolder[] {
  const path: ProjectFolder[] = [];
  const seen = new Set<number>();
  let current: FolderKey = id;
  while (current !== null && !seen.has(current)) {
    const folder = index.byId.get(current);
    if (!folder) break;
    seen.add(current);
    path.unshift(folder);
    current = folder.parent;
  }
  return path;
}

export function pathLabel(index: FolderIndex, id: FolderKey, separator = " / "): string {
  return folderPath(index, id)
    .map((f) => f.name)
    .join(separator);
}

export function descendantIds(index: FolderIndex, id: number): Set<number> {
  const found = new Set<number>();
  const stack: number[] = [id];
  while (stack.length) {
    const current = stack.pop() as number;
    for (const child of index.childrenOf.get(current) ?? []) {
      if (!found.has(child.id)) {
        found.add(child.id);
        stack.push(child.id);
      }
    }
  }
  return found;
}

export function isDescendant(index: FolderIndex, candidate: FolderKey, ancestor: number): boolean {
  return candidate !== null && descendantIds(index, ancestor).has(candidate);
}

export interface SubtreeCounts {
  projects: number;
  folders: number;
  /** Open detection incidents across every project beneath the folder. */
  openIncidents: number;
  /** Story maps beneath the folder; only counted when the listing is browsing stories. */
  stories?: number;
}

/**
 * Active projects, folders and open incidents anywhere beneath `id` (the
 * folder itself excluded). `storyCount`, when given, adds the story maps
 * filed anywhere beneath it.
 */
export function subtreeCounts(
  index: FolderIndex,
  projects: Project[],
  id: number,
  storyCount?: (folderId: number) => number
): SubtreeCounts {
  const ids = descendantIds(index, id);
  ids.add(id);
  let count = 0;
  let openIncidents = 0;
  projects.forEach((p) => {
    const folder = normalizeFolderId(index, p.folder);
    if (p.is_archived || folder === null || !ids.has(folder)) return;
    count += 1;
    openIncidents += p.open_incident_count ?? 0;
  });
  const counts: SubtreeCounts = { projects: count, folders: ids.size - 1, openIncidents };
  if (storyCount) counts.stories = storyCount(id);
  return counts;
}

/**
 * The nearest folder, from `id` up to the root, that is hidden from the map;
 * null when the whole chain is shown. A hidden folder hides everything in it.
 */
export function hiddenByFolder(index: FolderIndex, id: FolderKey): ProjectFolder | null {
  const seen = new Set<number>();
  let current: FolderKey = id;
  while (current !== null && !seen.has(current)) {
    const folder = index.byId.get(current);
    if (!folder) break;
    if (folder.map_visible === false) return folder;
    seen.add(current);
    current = folder.parent;
  }
  return null;
}

/** Drawn on the map: the folder itself and every folder above it are shown. */
export function folderMapVisible(index: FolderIndex, id: FolderKey): boolean {
  return hiddenByFolder(index, id) === null;
}

/** Drawn on the map: its own flag and every folder above it. */
export function projectMapVisible(index: FolderIndex, project: Project): boolean {
  return project.map_visible !== false && folderMapVisible(index, normalizeFolderId(index, project.folder));
}

/** What a drag carries: a project row or a folder row. */
export type DragItem =
  | { kind: "project"; id: string; folderId: FolderKey }
  | { kind: "folder"; id: number; parentId: FolderKey };

/**
 * Client-side mirror of the server's move rules, used to light up valid drop
 * targets and grey out the picker. The server still has the final say.
 */
export function canDrop(index: FolderIndex, item: DragItem, target: FolderKey): boolean {
  if (item.kind === "project") return item.folderId !== target;
  if (item.parentId === target) return false;
  if (target === null) return true;
  if (target === item.id) return false;
  return !isDescendant(index, target, item.id);
}
